import React, { useEffect, useMemo, useState } from "react";
import ResearchLayout, { useReveal } from "../ResearchLayout";
import { fetchPublicTeams, findTeam, memberImageUrl } from "../researchApi";

/* Admin must create a team named "IRB" (or any alias below) and add members. */
const IRB_TEAM_NAMES = ["IRB", "IRB Members", "Institutional Review Board", "Research IRB"];

export default function IRB() {
  useReveal();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchPublicTeams().then((data) => {
      if (!mounted) return;
      setTeams(data);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const members = useMemo(() => {
    const team = findTeam(teams, IRB_TEAM_NAMES);
    return team?.teamMembers || [];
  }, [teams]);

  return (
    <ResearchLayout>
      <section
        style={{
          padding: "100px 0 60px",
          background: "linear-gradient(135deg, #0d3b6e 0%, #076cec 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="rs-blob" style={{ width: 400, height: 400, background: "#a855f7", top: "-150px", right: "-100px" }} />
        <div className="rs-container rs-text-center" style={{ position: "relative", zIndex: 2 }}>
          <span className="rs-section-eyebrow" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(10px)" }}>
            Ethics & Oversight
          </span>
          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", fontWeight: 800, margin: "12px 0 16px" }}>Institutional Review Board</h1>
          <p style={{ fontSize: "1.05rem", maxWidth: 760, margin: "0 auto", opacity: 0.85, lineHeight: 1.7 }}>
            The IRB at Maroof International Hospital safeguards the rights, welfare, and well-being of every research participant.
          </p>

          <div style={{ display: "inline-flex", gap: 16, flexWrap: "wrap", justifyContent: "center", marginTop: 30 }}>
            <div
              style={{
                padding: "14px 24px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div style={{ fontSize: "0.7rem", opacity: 0.8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                IORG Number
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: 800, fontFamily: "monospace" }}>IORG0011525</div>
            </div>
            <div
              style={{
                padding: "14px 24px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div style={{ fontSize: "0.7rem", opacity: 0.8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                FWA Number
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: 800, fontFamily: "monospace" }}>FWA00032840</div>
            </div>
          </div>
        </div>
      </section>

      <section className="rs-section" style={{ paddingTop: 60 }}>
        <div className="rs-container">
          <div className="rs-text-center rs-reveal" style={{ marginBottom: 48 }}>
            <span className="rs-section-eyebrow">Committee</span>
            <h2 className="rs-section-title">
              IRB <span className="accent">Members</span>
            </h2>
            <p className="rs-section-sub">
              A multidisciplinary panel of senior physicians and specialists overseeing ethical review at Maroof.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 22,
            }}
          >
            {loading ? (
              <div style={{ gridColumn: "1 / -1", padding: "40px", textAlign: "center", color: "#6b7280" }}>
                <i className="fa-regular fa-spinner fa-spin" style={{ fontSize: "1.6rem", color: "var(--rs-primary)", marginBottom: 10 }}></i>
                <div>Loading IRB members…</div>
              </div>
            ) : members.length === 0 ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  padding: "50px 30px",
                  textAlign: "center",
                  background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
                  borderRadius: 18,
                  border: "1px dashed rgba(7, 108, 236,0.2)",
                }}
              >
                <h3 style={{ margin: "0 0 8px", color: "var(--rs-dark)", fontWeight: 800 }}>No IRB members yet</h3>
                <p style={{ color: "#6b7280", margin: 0 }}>
                  Create a team named "IRB" in the admin panel and add members to populate this section.
                </p>
              </div>
            ) : (
              members.map((m, i) => <IrbMemberCard key={m.id} m={m} index={i} />)
            )}
          </div>

          <div
            className="rs-reveal"
            style={{
              marginTop: 50,
              padding: 30,
              borderRadius: 18,
              background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
              border: "1px solid rgba(7, 108, 236,0.08)",
              textAlign: "center",
            }}
          >
            <p style={{ margin: "0 0 14px", color: "#374151", fontSize: "0.95rem" }}>Verify Maroof's IRB status on the OHRP database:</p>
            <a
              href="https://ohrp.cit.nih.gov/search/search.aspx?styp=bsc"
              target="_blank"
              rel="noopener noreferrer"
              className="rs-btn rs-btn-primary"
            >
              <i className="fa-regular fa-magnifying-glass"></i>
              Search OHRP Database
            </a>
          </div>
        </div>
      </section>
    </ResearchLayout>
  );
}

function IrbMemberCard({ m, index }) {
  const [imgError, setImgError] = useState(false);
  const showImage = m.hasImage && !imgError;
  return (
    <div className={`rs-card rs-reveal delay-${(index % 4) + 1}`} style={{ textAlign: "center" }}>
      <div
        style={{
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: showImage ? "#fff" : "var(--rs-grad-1)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "2rem",
          margin: "0 auto 16px",
          boxShadow: "0 8px 20px rgba(7, 108, 236,0.25)",
          transition: "transform 0.3s",
          overflow: "hidden",
        }}
      >
        {showImage ? (
          <img
            src={memberImageUrl(m.id)}
            alt={m.name}
            onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <i className="fa-regular fa-user-doctor"></i>
        )}
      </div>
      <h3 className="rs-card-title" style={{ marginBottom: 6 }}>
        {m.name}
      </h3>
      <p className="rs-card-text" style={{ fontSize: "0.85rem" }}>
        {m.designation}
      </p>
    </div>
  );
}
